/**
 * Terminal Storage - Persistent state across sessions
 */

export interface PlatformConnection {
  id: string;
  name: string;
  connected: boolean;
  apiKey?: string;
  apiSecret?: string;
  server?: string; // For MT5
  login?: string;  // For MT5
  password?: string; // For MT5
  balance?: number;
  currency?: string;
  lastSync?: string;
}

export interface StoredPosition {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  size: number;
  entryPrice: number;
  currentPrice: number;
  pnl: number;
  pnlPercent: number;
  platform: string;
  openedAt: string;
}

export interface TradeHistory {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  size: number;
  entryPrice: number;
  exitPrice: number;
  pnl: number;
  platform: string;
  openedAt: string;
  closedAt: string;
}

export interface TerminalState {
  platforms: PlatformConnection[];
  positions: StoredPosition[];
  tradeHistory: TradeHistory[];
  selectedPlatform: string;
  lastSymbol: string;
  lastTimeframe: string;
  runningStrategies: any[];
}

const STORAGE_KEY = 'kit_terminal_state';

// Default platforms
export const DEFAULT_PLATFORMS: PlatformConnection[] = [
  // Crypto Exchanges
  { id: 'binance', name: 'Binance', connected: false, currency: 'USDT' },
  { id: 'binance-futures', name: 'Binance Futures', connected: false, currency: 'USDT' },
  { id: 'bybit', name: 'Bybit', connected: false, currency: 'USDT' },
  { id: 'bybit-futures', name: 'Bybit Futures', connected: false, currency: 'USDT' },
  { id: 'kraken', name: 'Kraken', connected: false, currency: 'USD' },
  { id: 'coinbase', name: 'Coinbase', connected: false, currency: 'USD' },
  { id: 'kucoin', name: 'KuCoin', connected: false, currency: 'USDT' },
  { id: 'okx', name: 'OKX', connected: false, currency: 'USDT' },
  { id: 'gate', name: 'Gate.io', connected: false, currency: 'USDT' },
  { id: 'mexc', name: 'MEXC', connected: false, currency: 'USDT' },
  { id: 'bitget', name: 'Bitget', connected: false, currency: 'USDT' },
  
  // Forex/Stocks Brokers
  { id: 'mt5', name: 'MetaTrader 5', connected: false, currency: 'USD' },
  { id: 'mt4', name: 'MetaTrader 4', connected: false, currency: 'USD' },
  { id: 'ibkr', name: 'Interactive Brokers', connected: false, currency: 'USD' },
  { id: 'tradingview', name: 'TradingView Broker', connected: false, currency: 'USD' },
  { id: 'alpaca', name: 'Alpaca', connected: false, currency: 'USD' },
  { id: 'tradier', name: 'Tradier', connected: false, currency: 'USD' },
  
  // DeFi
  { id: 'dydx', name: 'dYdX', connected: false, currency: 'USDC' },
  { id: 'gmx', name: 'GMX', connected: false, currency: 'USDC' },
  { id: 'hyperliquid', name: 'Hyperliquid', connected: false, currency: 'USDC' },
  { id: 'jupiter', name: 'Jupiter (Solana)', connected: false, currency: 'SOL' },
  { id: 'raydium', name: 'Raydium', connected: false, currency: 'SOL' },
  
  // Paper Trading
  { id: 'paper', name: 'Paper Trading', connected: true, balance: 100000, currency: 'USD' },
];

export function loadTerminalState(): TerminalState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const state = JSON.parse(stored);
      // Merge with default platforms in case new ones were added
      const platformIds = state.platforms.map((p: any) => p.id);
      const newPlatforms = DEFAULT_PLATFORMS.filter(p => !platformIds.includes(p.id));
      return {
        ...state,
        platforms: [...state.platforms, ...newPlatforms],
      };
    }
  } catch (e) {
    console.error('Failed to load terminal state:', e);
  }
  
  return {
    platforms: DEFAULT_PLATFORMS,
    positions: [],
    tradeHistory: [],
    selectedPlatform: 'paper',
    lastSymbol: 'BTCUSDT',
    lastTimeframe: '60',
    runningStrategies: [],
  };
}

export function saveTerminalState(state: TerminalState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save terminal state:', e);
  }
}

export function connectPlatform(platforms: PlatformConnection[], platformId: string, credentials: Partial<PlatformConnection>): PlatformConnection[] {
  return platforms.map(p => {
    if (p.id === platformId) {
      return {
        ...p,
        ...credentials,
        connected: true,
        lastSync: new Date().toISOString(),
      };
    }
    return p;
  });
}

export function disconnectPlatform(platforms: PlatformConnection[], platformId: string): PlatformConnection[] {
  return platforms.map(p => {
    if (p.id === platformId) {
      return {
        ...p,
        connected: false,
        apiKey: undefined,
        apiSecret: undefined,
        server: undefined,
        login: undefined,
        password: undefined,
        balance: undefined,
      };
    }
    return p;
  });
}

export function addPosition(positions: StoredPosition[], position: Omit<StoredPosition, 'id' | 'openedAt'>): StoredPosition[] {
  const newPosition: StoredPosition = {
    ...position,
    id: Date.now().toString(),
    openedAt: new Date().toISOString(),
  };
  return [...positions, newPosition];
}

export function closePosition(
  positions: StoredPosition[], 
  history: TradeHistory[], 
  positionId: string, 
  exitPrice: number
): { positions: StoredPosition[]; history: TradeHistory[] } {
  const position = positions.find(p => p.id === positionId);
  if (!position) return { positions, history };
  
  const trade: TradeHistory = {
    id: Date.now().toString(),
    symbol: position.symbol,
    side: position.side,
    size: position.size,
    entryPrice: position.entryPrice,
    exitPrice,
    pnl: position.side === 'long' 
      ? (exitPrice - position.entryPrice) * position.size 
      : (position.entryPrice - exitPrice) * position.size,
    platform: position.platform,
    openedAt: position.openedAt,
    closedAt: new Date().toISOString(),
  };
  
  return {
    positions: positions.filter(p => p.id !== positionId),
    history: [...history, trade],
  };
}
